import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtTextarea from '@components/form/fragment/comp-fmt-textarea.js';
import CompFmtSelectbox from '@components/form/fragment/comp-fmt-selectbox.js';
import {ApiSave} from '@assets/js/api/commonApi.js';

const CompFormPlayerCompetitionApply = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [title] = useState('대회신청 선수정보 등록');
    const [dvsnParams, setDvsnParams] = useState({});
    const [wetclsParams, setWetclsParams] = useState([]);

    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheet_plyr_apply';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['comptnCd', 'dvsnCd', 'wetclsCd', 'armType'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            handleToggle();
            handleValue(p_params);
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleValue = (p_params) => {
        const dvsnCdEle = validateOpt.scopeEle?.querySelector('#dvsnCd');
        const wetclsCdEle = validateOpt.scopeEle?.querySelector('#wetclsCd');

        // Insert mode - enable all fields for selection
        if (dvsnCdEle) {
            setDvsnParams({});
        }
        if (wetclsCdEle) {
            setWetclsParams({});
        }

        // Set selected player names in the input field.
        let plyrNmArr = [];
        if (p_params && p_params.plyrAppList) {
            p_params.plyrAppList.forEach((item, idx) => {
                if (!plyrNmArr.includes(item.plyrNm)) {
                    plyrNmArr.push(item.plyrNm);
                }
            });
        }
        setParams({s_plyrNm: plyrNmArr.join(', '), s_plyrCnt: p_params.plyrAppList.length || 0, plyrAppList: p_params.plyrAppList || []});
    }

    const handleToggle = () => {
        if (validateOpt.scopeEle) {
            validateOpt.scopeEle.style.display = validateOpt.scopeEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        Object.keys(formObj).forEach(key => {
            const ele = validateOpt.scopeEle?.querySelector(`#${formObj[key].id}`);
            if (ele) { ele.value = ''; }
        });
    }

    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) { return; }
        if (params == null || params.comptnCd === '') { return; }

        // Create mtmkgCd from individual codes
        const mtmkgCd = `${params.comptnCd || ''}-${params.dvsnCd || ''}-${params.wetclsCd || ''}`;
        const saveParams = {
            plyrAppList : params.plyrAppList || [],
            armType : params.armType || '',
            mtmkgCd: mtmkgCd
        };

        // Fetch data from the server
        ApiSave('/sys/comptapplymng/save/players', saveParams, {
            msg : {confirm : '대회 신청을 하시겠습니까?', message : '대회 신청이 완료 되었습니다.'}
        }).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
                // Search for updated data
                props.onGridSearch();
            }
        });
    }

    const onValChange = (e) => {
        const { id } = e.target;
        // Validate the specific input field
        validateForm(validateOpt, id);
    }

    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }

    // Handle competition code change - fetch related division and weight class data
    const onComptnCdChange = (e) => {
        const { id, value } = e.target;
        setParams({ ...params, [id]: value, dvsnCd: '', wetclsCd: '' });

        if (value) {
            // Fetch division and weight class options for selected competition
            setDvsnParams({comptnCd: value});
            setWetclsParams({comptnCd: value});
        } else {
            // Clear Params if no competition selected
            setDvsnParams({});
            setWetclsParams({});
        }
    }

    const onChangeMtmkg = (e) => {
        const { id, value } = e.target;
        const rootEle = document.getElementById(rootId);

        // Get competition code
        const comptnCdElement = rootEle.querySelector('#comptnCd');
        const comptnCd = comptnCdElement ? comptnCdElement.value : '';

        // Get selected option text for wetclsCd
        const wetclsCdElement = rootEle.querySelector('#wetclsCd');
        const wetclsCd = wetclsCdElement ? wetclsCdElement.value : '';

        // Get selected option text for dvsnCd
        const dvsnCdElement = rootEle.querySelector('#dvsnCd');
        const dvsnCd = dvsnCdElement ? dvsnCdElement.value : '';

        // Create mtmkgCd from individual codes
        const mtmkgCd = comptnCd && dvsnCd && wetclsCd ? `${comptnCd}-${dvsnCd}-${wetclsCd}` : '';

        setParams({ ...params, [id]: value, mtmkgCd: mtmkgCd });
    }

    /****************************
     * Define form config
    *****************************/
    // Dynamic form configuration based on mode
    const formObj = {
        // Top section - Code selectors (dynamic disabled based on mode)
        comptnCd : formAtt({id: 'comptnCd', label: '대회', value: params['comptnCd'] || '', required: true, classCol: 'col-12'}),
        dvsnCd   : formAtt({id: 'dvsnCd', label: '부문', value: params['dvsnCd'] || '', required: true, classCol: 'col-4'}),
        wetclsCd : formAtt({id: 'wetclsCd', label: '체급', value: params['wetclsCd'] || '', required: true, classCol: 'col-4'}),
        armType  : formAtt({id: 'armType', label: '팔 유형', value: params['armType'] || '', required: true, classCol: 'col-4'}),
        
        // Player information
        s_plyrNm   : formAtt({id: 's_plyrNm', label: '선택된 선수('+params['s_plyrCnt']+'명)', value: params['s_plyrNm'] || '', disabled: true, classCol: 'col-12'}),
    };

    return (
        <>
            <div
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">{title}</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* Top section: Code selectors */}
                                            <div className="col-12">
                                                <div className="bg-light p-3 rounded">
                                                    <div className="row mb-3">
                                                        <CompFmtSelectbox
                                                            attrs={formObj.comptnCd}
                                                            grpCd={"COMPT"}
                                                            selectType={"S"}
                                                            onChange={onComptnCdChange}
                                                        />
                                                    </div>
                                                    <div className="row mb-3">
                                                        <CompFmtSelectbox
                                                            attrs={formObj.dvsnCd}
                                                            grpCd={"DVSNCD"}
                                                            selectType={"S"}
                                                            customParams={dvsnParams}
                                                            onChange={onChangeMtmkg}
                                                        />
                                                        <CompFmtSelectbox
                                                            attrs={formObj.wetclsCd}
                                                            grpCd={"WETCLSCD"}
                                                            selectType={"S"}
                                                            customParams={wetclsParams}
                                                            onChange={onChangeMtmkg}
                                                        />
                                                        <CompFmtSelectbox
                                                            attrs={formObj.armType}
                                                            grpCd={"GRP005"}
                                                            selectType={"S"}
                                                            onChange={onInputChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Player information */}
                                            <CompFmtTextarea
                                                attrs={formObj.s_plyrNm}
                                                onChange={onInputChange}
                                                onBlur={onValChange}
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSave}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CompFormPlayerCompetitionApply;